import React, { useEffect, useMemo, useState } from "react";

import { useAuth } from "@hl/shared-features/lib/features/auth";
import {
  ListFormToken,
  useListingCartContext,
} from "@hl/shared-features/lib/features/cart/ListingCartProvider";
import { expirationOptions } from "@hl/shared-features/lib/features/cart/useListingCart";
import { NoResults } from "@hl/shared-features/lib/features/collections/NoResults";
import MarketplaceFilterSortBar from "@hl/shared-features/lib/features/marketplace/components/MarketplaceFilterSortBar";
import { ListingDrawer } from "@hl/shared-features/lib/features/marketplace/drawers/ListingDrawer";
import {
  useGetUserForeignListingsQuery,
  useUserTokensQuery,
} from "@hl/shared-features/lib/features/marketplace/queries/marketplace.graphql.generated";
import { sortingOptionsWithMarketplace } from "@hl/shared-features/lib/utils/sortingOptions";
import {
  Box,
  Center,
  Group,
  SimpleGrid,
  Stack,
  useMantineTheme,
  Loader,
  Button,
  keyframes,
} from "@mantine/core";
import { useDebouncedValue, useMediaQuery } from "@mantine/hooks";
import { extend } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import InfiniteScroll from "react-infinite-scroll-component";

import UserCollectedListSkeleton from "../profile/UserCollectedListSkeleton";
import { OnLoadProps } from "../profile/UserTabs";
import { GetCollectedByUserAddressQuery } from "../profile/queries/profile.graphql.generated";

import CollectedCard from "./CollectedCard";
import FilterSidebarCollected from "./components/FilterSidebarCollected";
extend(timezone);
extend(advancedFormat);
extend(customParseFormat);

export type UserToken =
  GetCollectedByUserAddressQuery["getCollectedByWalletAddress"]["items"][0];

const filteringOptions = [
  { label: "Items for sale", value: "sale" },
  { label: "Items with offers", value: "offers" },
  { label: "Items not for sale", value: "not_sale" },
];

export enum CardView {
  Default = "default",
  LineItem = "lineItem",
}

const shimmer = keyframes`
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: 100% 0;
  }
`;

export const shimmerStyles = (isDark = false) => ({
  position: "relative",

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(
      90deg,
      transparent 40%,
      rgba(${isDark ? "0, 0, 0, 0.05" : "255, 255, 255, 0.2"}) 50%,
      transparent 60%
    )`,
    backgroundSize: "200% 100%",
    animation: `${shimmer} 2s infinite linear`,
    borderRadius: "inherit",
  },
});

export const Collected = ({ onLoad, active, slug, isOwner }: OnLoadProps) => {
  const theme = useMantineTheme();
  const { walletAddress } = useAuth();
  const { dispatch, state } = useListingCartContext();
  const { opened: cartOpened } = state;
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs - 1}px)`);
  const [showFilters, setShowFilters] = useState(false);
  const [firstTotalCount, setFirstTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>();
  const [searchString, setSearchString] = useState<string>("");
  const [debouncedSearchString] = useDebouncedValue(searchString, 250);
  const [sorting, setSorting] = useState<string | null>(
    sortingOptionsWithMarketplace[2].value
  );
  const [hasRelisted, setHasRelisted] = useState(false);
  const selectedSort = useMemo(
    () =>
      sortingOptionsWithMarketplace.find(
        (sortingOption) => sortingOption.value === sorting
      ),
    [sorting]
  );
  const {
    data: foreignListings,
    loading: foreignListingsLoading,
    refetch: refetchForeignListings,
  } = useGetUserForeignListingsQuery({
    variables: {
      after: null,
      first: 100,
    },
    skip: !walletAddress || !isOwner,
  });

  const ethSavings =
    foreignListings?.getUserForeignListings.relistingSavings.native;

  useEffect(() => {
    if (!state.opened) {
      setHasRelisted(false);
    }
  }, [state.opened]);
  const hasForeignListings =
    !!foreignListings?.getUserForeignListings.edges.length;
  const withOffers = statusFilter === "offers" ? true : null;
  const withListings =
    statusFilter === "sale" ? true : statusFilter === "not_sale" ? false : null;
  const { data, loading, fetchMore, error, refetch } = useUserTokensQuery({
    variables: {
      walletAddress: slug!,
      minted: null,
      attributes: null,
      withOffers,
      withListings,
      tokenNameOrId: debouncedSearchString,
      sortBy: selectedSort!.sortBy,
      sortDirection: selectedSort!.sortDirection,
      after: null,
      first: 12,
    },
    skip: !slug,
  });

  const relist = () => {
    setHasRelisted(true);
    // @ts-ignore
    const tokens: ListFormToken[] =
      foreignListings?.getUserForeignListings.edges
        .filter((listing) => !!listing.token)
        .map((listing) => {
          return {
            id: listing.reservoirId,
            tokenId: listing.tokenId,
            chainId: listing.chainId,
            address: listing.contractAddress,
            quantity: listing.quantityRemaining,
            royaltyBps: 0,
            expirationOption: expirationOptions[5],
            editionId: listing.collection?.onchainId?.split(":")[2],
            price: listing.price.amount.native,
            token: {
              ...listing,
              ...listing.token,
              tokenListing: listing,
            },
            relistingId: listing.reservoirId,
          };
        }) || [];
    dispatch({ type: "ADD_TOKENS", payload: tokens });
    dispatch({ type: "OPEN_DRAWER" });
  };

  useEffect(() => {
    const totalCount = data?.getUserTokens.pageInfo.totalCount;
    if (totalCount != null) {
      onLoad?.({ totalCount });
    }
    if (totalCount && totalCount > firstTotalCount) {
      setFirstTotalCount(totalCount);
    }
  }, [data?.getUserTokens.pageInfo.totalCount]);

  const nextCursor = data?.getUserTokens?.pageInfo.endCursor;
  const hasNextPage = data?.getUserTokens?.pageInfo?.hasNextPage;

  const subject = slug === walletAddress ? "You haven't" : "This user hasn't";

  const nfts = data?.getUserTokens.edges;
  const noResultsTitle = useMemo(() => {
    if (statusFilter === "offers") {
      return `${subject} received any offers on ${
        slug === walletAddress ? "your" : "their"
      } tokens`;
    }
    if (statusFilter === "sale") {
      return subject + ` put any items on sale`;
    }

    if (firstTotalCount) {
      return "No results found. Please try again.";
    }

    return `${subject} minted any tokens on Highlight`;
  }, [statusFilter, firstTotalCount, slug, walletAddress]);

  const noResultsDescription = useMemo(() => {
    if (statusFilter === "offers") {
      return `${subject} received any offers on ${
        slug === walletAddress ? "your" : "their"
      } tokens`;
    }
    if (statusFilter === "sale") {
      return `${subject} put any items on sale`;
    }

    if (firstTotalCount) {
      return "";
    }

    return `Once ${
      slug === walletAddress ? "you" : "they"
    } do, they'll appear here.`;
  }, [firstTotalCount, slug, walletAddress]);

  if (!active) return null;

  return (
    <Stack pb={cartOpened ? 460 : 0}>
      <ListingDrawer
        onCompleted={() =>
          setTimeout(() => {
            refetch();
            refetchForeignListings();
          }, 3000)
        }
      />
      <MarketplaceFilterSortBar
        setSorting={setSorting}
        setShowFilters={setShowFilters}
        sortingOptions={sortingOptionsWithMarketplace}
        sorting={sorting}
        setSearchString={setSearchString}
        searchString={searchString}
        hasFilterToggle={true}
        customViewElement={
          !!walletAddress &&
          isOwner &&
          hasForeignListings && (
            <Button
              onClick={relist}
              disabled={foreignListingsLoading || hasRelisted}
              styles={{
                // @ts-ignore
                root: {
                  ...(!hasRelisted ? shimmerStyles() : {}),
                  backgroundColor: theme.colors.primaryButton[0],
                  transition: "background-color 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: theme.colors.primaryButton[1],
                  },
                },
                label: {
                  position: "relative",
                  zIndex: 1,
                },
              }}
            >
              {foreignListingsLoading
                ? "Loading listing value"
                : hasRelisted
                ? "Re-listings added to cart"
                : `Relist to save ${ethSavings ? ethSavings + " ETH" : ""}`}
            </Button>
          )
        }
      />
      <Group spacing={40} mt={8} noWrap align="flex-start" w="100%">
        {showFilters && (
          <FilterSidebarCollected
            setStatusFilter={setStatusFilter}
            statusFilter={statusFilter}
            filteringOptions={filteringOptions}
            showFilters={showFilters}
            closeFilters={() => setShowFilters(false)}
          />
        )}
        {loading && !nextCursor ? (
          <LoadingItems view={CardView.Default} />
        ) : nfts?.length ? (
          <InfiniteScroll
            style={{
              overflow: "hidden",
              transition: "all 0.4s ease-in-out",
              flexGrow: 1,
              width: "100%",
              display: isMobile && showFilters ? "none" : "block",
            }}
            scrollThreshold={1}
            dataLength={nfts.length}
            next={() =>
              hasNextPage &&
              fetchMore({
                variables: { after: nextCursor },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                  fetchMoreResult!.getUserTokens.edges = [
                    ...(previousResult?.getUserTokens?.edges ?? []),
                    ...(fetchMoreResult?.getUserTokens?.edges ?? []),
                  ];
                  return fetchMoreResult;
                },
              })
            }
            hasMore={!!nextCursor}
            loader={<LoadingItems view={CardView.Default} showHeader={false} />}
          >
            <SimpleGrid
              mb={30}
              cols={4}
              spacing={40}
              verticalSpacing={56}
              breakpoints={[
                { maxWidth: "lg", cols: 3 },
                { maxWidth: "md", cols: 2 },
                { maxWidth: "sm", cols: 1, verticalSpacing: 40 },
              ]}
            >
              {!nfts.length && !loading && (
                <Center p="lg" w="100%">
                  No data
                </Center>
              )}
              {nfts.map((token) => (
                <CollectedCard
                  key={token.id}
                  token={token}
                  isOwner={isOwner}
                  onCompleted={() => setTimeout(refetch, 3000)}
                />
              ))}
            </SimpleGrid>
          </InfiniteScroll>
        ) : !!error ? (
          <NoResults
            title="Something went wrong"
            description="Please try again later"
          />
        ) : (
          <NoResults
            title={noResultsTitle}
            description={noResultsDescription}
          />
        )}
      </Group>
    </Stack>
  );
};

export const Loading = () => (
  <Center p="lg" w="100%">
    <Loader />
  </Center>
);

const LoadingItems = ({
  view,
  showHeader = true,
}: {
  view: CardView;
  showHeader?: boolean;
}) => (
  <Box
    sx={{
      flexGrow: 1,
      width: "100%",
    }}
  >
    <UserCollectedListSkeleton view={view} showHeader={showHeader} />
  </Box>
);
